import React from 'react';
import { Message, toaster } from 'rsuite';
import { toasterManager } from '../utils/toasterManager';

interface RefreshProps {
    successCallback?: () => void;
    declineCallback?: () => void;
}

export const toastRefreshMessage = (props: RefreshProps): JSX.Element => {
    const { successCallback, declineCallback } = props;
    return (
        <Message type="info" closable header="Do you want to refresh session?" onClose={declineCallback}>
            <div className="d-flex align-items-center justify-content-center">
                <button className="btn btn-sm fw-bolder btn-secondary" style={{ margin: '0 10px' }} onClick={successCallback}>Yes</button>
                <button className="btn btn-sm fw-bolder btn-primary" style={{ margin: '0 10px' }} onClick={declineCallback}>No</button>
            </div>
        </Message>
    );
};

interface Props {
    text: string;
    type: 'error' | 'success';
    closable?: boolean;
}

const toastMessage = (props: Props): JSX.Element => {
  const { text, type, closable } = props;
  return (
    <Message type={type} closable={closable}>
      {text}
    </Message>
  );
};

export default toastMessage;

interface IAlertProps {
  title: string;
  duration?: number;
  closable?: boolean;
}

export const successAlert = (props: IAlertProps): string => {
  const { title, duration = 4000, closable } = props;
  return toasterManager.success(
    <Message type="success" closable={closable}>
      {title}
    </Message>,
    {
      placement: 'topEnd',
      duration: duration
    }
  );
};

export const errorAlert = (props: IAlertProps): string => {
  const { title, duration = 4000, closable = true } = props;

  console.log('errorAlert called with:', title, duration, closable);

  // Make closable default to true for consistency with showErrorToast
  // Add a check to ensure title is a string
  const errorMessage = typeof title === 'string' ? title : 'Something went wrong';

  return toasterManager.error(
    <Message type="error" closable={closable}>
      {errorMessage}
    </Message>,
    {
      placement: 'topEnd',
      duration: duration
    }
  );
};

// Add these new helper functions that don't clear existing toasts
export const errorAlertWithoutClearing = (props: IAlertProps): string => {
  const { title, duration = 4000, closable = true } = props;
  const errorMessage = typeof title === 'string' ? title : 'Something went wrong';

  return toasterManager.errorWithoutClearing(
    <Message type="error" closable={closable}>
      {errorMessage}
    </Message>,
    {
      placement: 'topEnd',
      duration: duration
    }
  );
};

export const successAlertWithoutClearing = (props: IAlertProps): string => {
  const { title, duration = 4000, closable } = props;
  return toasterManager.successWithoutClearing(
    <Message type="success" closable={closable}>
      {title}
    </Message>,
    {
      placement: 'topEnd',
      duration: duration
    }
  );
};

// Helper functions for common toast patterns
export const showSuccessToast = (message: string, duration = 4000, closable?: boolean): string => {
  return toasterManager.success(
    <Message type="success" closable={closable}>
      {message}
    </Message>,
    {
      placement: 'topEnd',
      duration: duration
    }
  );
};

export const showErrorToast = (message: string, duration = 4000, closable = true): string => {
  return toasterManager.error(
    <Message type="error" closable={closable}>
      {message}
    </Message>,
    {
      placement: 'topEnd',
      duration: duration
    }
  );
};

export const showWarningToast = (message: string, duration = 4000, closable?: boolean): string => {
  return toasterManager.warning(
    <Message type="warning" closable={closable}>
      {message}
    </Message>,
    {
      placement: 'topEnd',
      duration: duration
    }
  );
};

export const showInfoToast = (message: string, duration = 4000, closable?: boolean): string => {
  return toasterManager.info(
    <Message type="info" closable={closable}>
      {message}
    </Message>,
    {
      placement: 'topEnd',
      duration: duration
    }
  );
};

// Clear all toasts
export const clearAllToasts = (): void => {
  toasterManager.clearAll();
};

// Direct error alert that doesn't use the queue system
export const errorAlertDirect = (props: IAlertProps): string => {
  const { title, duration = 10000, closable = true } = props; // Use 10 seconds as default
  const errorMessage = typeof title === 'string' ? title : 'Something went wrong';

  return toasterManager.errorDirect(
    <Message type="error" closable={closable}>
      {errorMessage}
    </Message>,
    {
      placement: 'topEnd',
      duration: duration
    }
  );
};

// Direct success alert that doesn't use the queue system
export const successAlertDirect = (props: IAlertProps): string => {
  const { title, duration = 4000, closable } = props;

  return toasterManager.successDirect(
    <Message type="success" closable={closable}>
      {title}
    </Message>,
    {
      placement: 'topEnd',
      duration: duration
    }
  );
};
